/**
 * Workspace invitations — the API surface for SPEC-01 Stage 3.
 *
 * NOT `./invitations.ts`. That file is CARD invitations (who is on a card), a
 * different table and a different question; two things called "invitations"
 * in one folder is how a reader picks the wrong one.
 *
 * `acceptUrl` arrives ONLY from `create` and `resend`. The gateway stores a
 * sha256 of the token, so `list` structurally cannot return a link and no
 * amount of UI wanting one will change that — a screen that needs the link
 * again must resend, which mints a new one and kills the old.
 */
import type {
  AcceptInvitationResult,
  AcceptInvitationInput,
  CreateOrgInvitationInput,
  CreatedOrgInvitation,
  InvitationPreview,
  OrgInvitation,
} from '@chaos/types/orgs';

import { apiJson } from './client';

export const orgInvitationsApi = {
  list(orgId: string): Promise<OrgInvitation[]> {
    return apiJson<{ invitations: OrgInvitation[] }>(`/orgs/${orgId}/invitations`).then(
      (r) => r.invitations,
    );
  },

  create(orgId: string, input: CreateOrgInvitationInput): Promise<CreatedOrgInvitation> {
    return apiJson<CreatedOrgInvitation>(`/orgs/${orgId}/invitations`, {
      method: 'POST',
      json: input,
    });
  },

  /** Mints a NEW link and kills the previous one. */
  resend(orgId: string, invitationId: string): Promise<CreatedOrgInvitation> {
    return apiJson<CreatedOrgInvitation>(`/orgs/${orgId}/invitations/${invitationId}/resend`, {
      method: 'POST',
    });
  },

  revoke(orgId: string, invitationId: string): Promise<{ invitation: OrgInvitation }> {
    return apiJson<{ invitation: OrgInvitation }>(`/orgs/${orgId}/invitations/${invitationId}`, {
      method: 'DELETE',
    });
  },
};

/**
 * The two PUBLIC calls, used by /signup while the caller has no session.
 * They live here rather than in auth.ts because they are the invitation lane;
 * the gateway mounts them beside the login routes for the same reason the
 * caller cannot be authenticated yet.
 */
export const publicInvitationApi = {
  preview(token: string): Promise<InvitationPreview> {
    return apiJson<{ invitation: InvitationPreview }>(
      `/auth/invitation/${encodeURIComponent(token)}`,
    ).then((r) => r.invitation);
  },

  accept(input: AcceptInvitationInput): Promise<AcceptInvitationResult> {
    return apiJson<AcceptInvitationResult>('/auth/accept-invitation', {
      method: 'POST',
      json: input,
    });
  },
};

export const orgInvitationKeys = {
  all: ['org-invitations'] as const,
  list: (orgId: string) => [...orgInvitationKeys.all, orgId] as const,
};
